import angular from 'angular';
import { $q } from '@uirouter/core';
import SignalRService from '@/shared/services/SignalRService';
import moment from 'moment';
import DocumentService from '@/shared/services/DocumentService';
import getFieldIdForCompliancy from '@/modules/compliancy/getFieldIdForCompliancy';
import eventBus from 'pubsub-js';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import { RegistrationGroup } from '../../registrations/model/registrationGroup';
import { compactRelation } from '@/ajs/common/Filters/relationNameFilter';
import dataCacheService from '@/shared/services/DataCacheService';
import { ConnectPlatform } from '@/shared/connectPlatformConstants';
import { getFirstSelectedInvoiceStreamId } from '@/shared/components/registration/registrationHelpers';

const templateTypes = [
  3, // Assignment letters
  10, // Authorization letters
  13, // EID
  18, // Questionnaire,
  21, // Contract - Rental
  22, // Inventory
  23, // Provision note
  26, // CompositionStatement
  27, // TerminationConfirmation
  30, // Quotation
  99, // Others
];

angular.module('cxRelations').directive('cxRelationFile', function() {
  return {
    restrict: 'E',
    template: require('./relationFile.html'),
    scope: {
      relId: '@',
    },
    controllerAs: 'vm',
    bindToController: true,
    controller: [
      '$filter',
      'relBindSvc',
      '$anchorScroll',
      'userData',
      '$timeout',
      'flySvc',
      'dataCache',
      'ModalService',
      '$http',
      'appUrls',
      '$q',
      '$scope',
      'checkSecurity',
      '$stateParams',
      '$state',
      'invoiceDialogsSvc',
      'relBudgetSvc',
      'dialogsSvc',
      'regDialogsSvc',
      'registrationService',
      'invoiceStreamNamesSvc',
      function(
        $filter,
        relBindSvc,
        $anchorScroll,
        userData,
        $timeout,
        flySvc,
        dataCache,
        ModalService,
        $http,
        appUrls,
        $q,
        $scope,
        checkSecurity,
        $stateParams,
        $state,
        invoiceDialogsSvc,
        relBudgetSvc,
        dialogsSvc,
        regDialogsSvc,
        registrationService,
        invoiceStreamNamesSvc,
      ) {
        this.$onInit = function() {
          var vm = this;
          vm.error = false;
          vm.relation = relBindSvc.relations[vm.relId];
          vm.sections = relBindSvc.sections;
          vm.isPerson = vm.relation.type !== 0; // ok
          vm.moreActionsRunning = false;
          vm.companyWebEnabled = false;
          vm.companyInfoEnabled = false;
          vm.openTheBoxEnabled = false;
          vm.estoxEnabled = false;
          vm.hasVisionPlanner = false;
          vm.kboEnabled = false;
          vm.listeningOnDocumentUpdate = false;

          vm.showBudgetInfo = () => relBudgetSvc.showBudgetInfo(vm.relId);

          dataCache.ready().then(() => {
            vm.companySettings = dataCache.getCompanySettings(vm.relation.companyId);

            const searchProviders = dataCache.getArray('relationSearchProviders');
            if (vm.relation.type !== 2) {
              vm.estoxEnabled = searchProviders.some(x => x === 2); // external relation provider 2 is estox
              vm.companyWebEnabled = searchProviders.some(x => x === 3); // external relation provider 3 is company web
              vm.companyInfoEnabled = searchProviders.some(x => x === 4);
              vm.openTheBoxEnabled = searchProviders.some(x => x === 5);
              vm.kboEnabled =
                searchProviders.some(x => x === 0) && // external relation provider 0 is kbo
                vm.relation.isActive &&
                vm.relation.country === 'BE';
            }
            vm.hasTwikey =
              dataCache.getCompanySettings(vm.relation.companyId).invoicePaymentProvider === 2;
            vm.hasMollie =
              dataCache.getCompanySettings(vm.relation.companyId).invoicePaymentProvider === 3;
            vm.hasGrub = dataCacheService.hasConnectPlatform(
              vm.relation.companyId,
              ConnectPlatform.Grub,
            );
            vm.hasSharepoint = dataCacheService.hasConnectPlatform(
              vm.relation.companyId,
              ConnectPlatform.SharepointAutoSync,
            );
            vm.hasVisionPlanner = dataCacheService.hasConnectPlatform(
              vm.relation.companyId,
              ConnectPlatform.VisionPlanner,
            );
          });

          eventBus.subscribe('relationTypeChanged', function() {
            $timeout(function() {
              refreshCompliancy();
            }, 100);
          });

          vm.getRelationKPIData = function() {
            if (!checkSecurity('PlannedTask:r') && !checkSecurity('Todo:r')) return;
            $http
              .get(`${appUrls.tasks}/api/plannedtasks/RelationKPI/${vm.relation.id}`)
              .then(function(response) {
                var result = response.data; // Extract response data
                vm.relation.numberOfTasks = result.totalTasks || 0;
                vm.relation.numberOfTodos = result.totalTodos || 0;
                vm.relation.expiredTodos = result.expiredTodos || 0;

                vm.relation.allExpired =
                  vm.relation.numberOfTodos > 0 &&
                  vm.relation.numberOfTodos === vm.relation.expiredTodos;
                vm.relation.anyExpired = vm.relation.expiredTodos > 0;
              })
              .catch(function(error) {
                console.error('Error refreshing data:', error);
                // Handle error scenario if needed
                vm.relation.numberOfTasks = 0;
                vm.relation.numberOfTodos = 0;
                vm.relation.expiredTodos = 0;
              });
          };

          vm.getRelationKPIData();

          if (vm.relation.eidPhotoDocumentId) {
            DocumentService.getLink(vm.relation.eidPhotoDocumentId)
              .then(result => {
                vm.relation.eidPhotoUrl = result;
              })
              .catch(() => (vm.relation.eidPhotoUrl = null));
          }

          function getLastContactDate() {
            if (!checkSecurity('InteractionsOwn:r')) return;
            $http
              .get(appUrls.interactions + '/api/interactions/lastcontact/' + vm.relation.id)
              .then(function(response) {
                var result = response.data;
                if (result) {
                  vm.relation.lastContactDate = moment(result).toDate();
                } else {
                  vm.relation.lastContactDate = null;
                }
              })
              .catch(function() {
                vm.relation.lastContactDate = null;
              });
          }
          getLastContactDate();

          function refreshCompliancy() {
            $http
              .get(appUrls.api + '/api/Compliance/Relation/' + vm.relation.id)
              .then(function(response) {
                var result = response.data;
                if (!result) return;
                vm.relation.compliance = result
                  .map(x => {
                    return {
                      ...x,
                      label: $filter('translateList')(x.type, 'complianceType', x.type),
                    };
                  })
                  .sort(dynamicSort('label'));
              })
              .catch(function() {
                vm.relation.compliance = null;
              });
          }
          refreshCompliancy();

          function sectionContainsField(section, fieldId) {
            if (section.lists)
              for (var l = 0; l < section.lists.length; l++) {
                if (section.lists[l].id === fieldId) return section.lists[l];
              }
            if (section.columns)
              for (var c = 0; c < section.columns.length; c++) {
                for (var e = 0; e < section.columns[c].length; e++) {
                  if (section.columns[c][e].id === fieldId) return section.columns[c][e];
                }
              }
            return null;
          }

          vm.findFieldInSections = function(fieldId) {
            if (!vm.relation || !vm.relation.sections) return null;

            for (var s = 0; s < vm.relation.sections.length; s++) {
              var mainSection = vm.relation.sections[s];
              var field = sectionContainsField(mainSection, fieldId);
              if (!!field) return { mainSection, field, subSection: null };
              if (mainSection.sections)
                for (var ss = 0; ss < mainSection.sections.length; ss++) {
                  var subSection = mainSection.sections[ss];
                  var field = sectionContainsField(subSection, fieldId);
                  if (!!field) return { mainSection, field, subSection: subSection };
                }
            }
            return null;
          };

          vm.gotoField = function(fieldIdArg, retryCount) {
            if (!vm.relation) return;
            if (vm.relation.sectionConfigKey && !vm.relation.sections) {
              if (retryCount > 5) return $q.reject();
              // Still loading sections, wait for 500ms and try again
              return $timeout(function() {
                return vm.gotoField(fieldIdArg, (retryCount || 0) + 1);
              }, 500);
            }
            //Split fieldId and optional custom anchor
            const [fieldId, customAnchor] = (fieldIdArg || '').split(';');
            var fieldInfo = vm.findFieldInSections(fieldId);
            if (!fieldInfo) return $q.reject();
            return vm.openSection(fieldInfo.mainSection).then(function() {
              return $timeout(function() {
                if (customAnchor) {
                  //After opening section with field, scroll to custom anchor
                  $anchorScroll(customAnchor);
                } else {
                  const sectionId = (fieldInfo.subSection || fieldInfo.mainSection).id;
                  $anchorScroll(`${sectionId}-${fieldId}`);
                }
              }, 500);
            });
          };
          vm.openSection = function(section) {
            if (!vm.relation || !section) return $q.reject();
            if (typeof section === 'string') {
              if (!vm.relation.sections) return $q.reject();
              section = vm.relation.sections.find(x => x.id === section);
            }
            if (!section.id) return $q.reject();

            if (vm.relation.collapsedSections[section.id])
              vm.relation.collapsedSections[section.id] = false;
            if (!section.loaded) {
              return relBindSvc.loadSection(vm.relId, section.id).then(() => {
                vm.relation.collapsedSections[section.id] = false;
              });
            }
            return $q.when();
          };

          vm.goToSectionLinks = function() {
            var linksSectionId = vm.relation.linksSectionId;
            vm.openSection(linksSectionId);
            $timeout(function() {
              $anchorScroll(linksSectionId);
            }, 250);
          };

          var addressList = [];
          if (
            vm.relation[vm.relation.addressesFieldId] &&
            vm.relation[vm.relation.addressesFieldId].value
          )
            addressList = vm.relation[vm.relation.addressesFieldId].value;
          var getAddress = function(medium) {
            // the logic for: wich address (phone/email) to display.
            for (var t = 0; t < addressList.length; t++) {
              if (addressList[t].medium === medium && addressList[t].isMain === true)
                return addressList[t].addressData;
            }
            // If there are no addresses (of given type) or mainAddress is not set
            return null;
          };

          vm.getPhone = function() {
            if (vm.relation.contactPhone) return vm.relation.contactPhone;
            return getAddress(2);
          };

          vm.getEmail = function() {
            if (vm.relation.contactEmail) return vm.relation.contactEmail;
            return getAddress(1);
          };

          vm.openInteractionsMailModal = function() {
            $state.go('interactions', {
              relationId: vm.relation.id,
              openMailModal: true,
            });
          };

          vm.getMobile = function() {
            if (vm.relation.contactMobile) return vm.relation.contactMobile;
            return getAddress(4);
          };
          vm.toggleDashboard = function() {
            vm.isDashboardCollapsed = !vm.isDashboardCollapsed;
            userData.update('relSections', function(value) {
              if (value == null) value = {};
              value['relDashboard'] = !vm.isDashboardCollapsed;
              return value;
            });
          };

          vm.isDashboardCollapsed = !userData.get('relSections', {}).relDashboard;

          var filterEmpty = function(fieldId) {
            if (typeof vm.relation[fieldId] === 'undefined') return false;
            var value = vm.relation[fieldId].value;
            if (typeof value === 'undefined' || value === null) return false; //Null values==> false

            var fieldType = vm.relation[fieldId].type;
            if (!fieldType) return true;

            switch (fieldType) {
              case 1: //Integer
              case 2: //Decimal
              case 41: //Percentage
                return !isNaN(value) && value != 0;
              case 3: //Date
                const date = moment.utc(value).toDate();
                return !isNaN(date) && date > new Date('1901-01-01T00:00:00Z');
              case 5:
              case 6:
              case 7:
              case 8:
              case 18: //Strings
                return ('' + value).trim().length > 0;
              default:
                return true;
            }
          };

          vm.calcCompleteness = function() {
            if (!vm.relation.completeness) return 0;
            var filledIn = vm.relation.completeness.filter(filterEmpty).length;
            return (filledIn * 100.0) / vm.relation.completeness.length;
          };

          vm.missingFields = function() {
            //TFS 5693
            if (!vm.relation.completeness || !vm.relation.completeness.length) return;
            const fields = vm.relation.completeness.filter(function(item) {
              return !filterEmpty(item);
            });
            const list = fields
              .map(function(fieldId) {
                return $filter('translateList')(fieldId, 'field');
              })
              .join(', ');

            if (list !== '') {
              const firstId = fields[0];
              flySvc.action(
                null,
                'msgFieldsNeededCompleteFile',
                function() {
                  vm.gotoField(firstId);
                },
                null,
                null,
                {
                  list: list + '.',
                },
              );
            }
          };

          vm.refreshOpenSections = () => {
            if (!vm.relation || !vm.relation.collapsedSections) return;

            // vm.relation.collapsedSections is an object with key section and value true/false indicating whether or not its collapsed
            const entries = Object.entries(vm.relation.collapsedSections);

            const openSections = entries
              .filter(([key, value]) => value === false)
              .map(([key, value]) => key);

            if (openSections.length === 0) return;
            relBindSvc.refreshSections(vm.relation.id, openSections);
          };

          vm.gotoCompliancyField = function(compliance) {
            const fieldId = getFieldIdForCompliancy(
              compliance.type,
              compliance.status,
              vm.relation.type,
            );
            if (fieldId) vm.gotoField(fieldId);
          };

          if ($stateParams.gotoField) {
            vm.gotoField($stateParams.gotoField);
          }

          vm.openTwikeyContractTemplateModal = () => {
            ModalService.showModal({
              template: require('../dialogs/inviteRelationDirectDebitDialog.html'),
              controller: 'InviteRelationDirectDebitDialogCtrl',
              controllerAs: 'vm',
              locationChangeSuccess: false,
              inputs: {
                relationId: vm.relation.id,
                companyId: vm.relation.companyId,
              },
            }).then(function(modal) {
              modal.element.modal();
              modal.element.on('hidden.bs.modal', function(e) {
                modal.element.off(e); //Remove event
                modal.element.remove(); //Remove element
              });
            });
          };

          vm.openMollieDirectDebitDialog = () => {
            ModalService.showModal({
              template: require('../dialogs/mollieDirectDebitDialog.html'),
              controller: 'MollieDirectDebitDialogCtrl',
              controllerAs: 'vm',
              locationChangeSuccess: false,
              inputs: {
                relationId: vm.relation.id,
              },
            }).then(function(modal) {
              modal.element.modal();
              modal.element.on('hidden.bs.modal', function(e) {
                modal.element.off(e); //Remove event
                modal.element.remove(); //Remove element
              });
            });
          };

          vm.createRelationInVisionPlanner = () => {
            ModalService.showModal({
              template: require('../dialogs/visionPlannerCreateAdministrationDialog.html'),
              controller: 'VisionPlannerCreateAdministrationDialogCtrl',
              controllerAs: 'vm',
              locationChangeSuccess: false,
              inputs: {
                relationId: vm.relation.id,
              },
            }).then(function(modal) {
              modal.element.modal();
              modal.element.on('hidden.bs.modal', function(e) {
                modal.element.off(e); //Remove event
                modal.element.remove(); //Remove element
              });
            });
          };

          vm.deleteRelation = () => {
            dialogsSvc
              .showYesNo('msgConfirmDeleteTitle', 'msgConfirmDeleteRelation')
              .then(function() {
                $http.delete(`${appUrls.api}/api/relation/delete/${vm.relation.id}`).then(
                  ({ data: setToInactive }) => {
                    if (setToInactive) {
                      flySvc.info('msgRelationSetToInactive');
                    } else {
                      flySvc.success();
                    }
                    $state.go('relations.overview');
                  },
                  function(error) {
                    flySvc.error(error);
                  },
                );
              });
          };

          vm.toggleComplianceInapplicable = compliance => {
            $http
              .put(
                appUrls.api +
                  '/api/compliance/ToggleInapplicable?relationId=' +
                  vm.relation.id +
                  '&complianceType=' +
                  compliance.type,
              )
              .then(function(response) {
                flySvc.success('msgConfirmChange');
                refreshCompliancy();
              })
              .catch(function(error) {
                flySvc.error(error);
              });
          };

          vm.createDocument = function() {
            ModalService.showModal({
              template: require('../dialogs/generateRelationDocumentDialog.html'),
              controller: 'GenerateRelationDocumentDialogCtrl',
              controllerAs: 'vm',
              locationChangeSuccess: false,
              inputs: {
                relationId: vm.relation.id,
                companyId: vm.relation.companyId,
                language: vm.relation.languageCode,
                relationType: vm.relation.type, // relation type
                templateTypes,
              },
            }).then(function(modal) {
              modal.element.modal();
              modal.element.on('hidden.bs.modal', function(e) {
                modal.element.off(e); //Remove event
                modal.element.remove(); //Remove element
              });

              modal.close.then(function(result) {
                if (result && result.document) {
                  if (result.openEmailDialog) {
                    vm.openEmailDocumentDialog(result.document, true);
                  } else {
                    vm.refreshOpenSections();
                    flySvc.success();
                  }
                }
              });
            });
          };

          vm.openEmailDocumentDialog = function(document, asPdf) {
            ModalService.showModal({
              template: require('../dialogs/mailDocumentDialog.html'),
              controller: 'MailDocumentDialogCtrl',
              controllerAs: 'vm',
              locationChangeSuccess: false,
              inputs: {
                document: document,
                asPdf: asPdf,
                relationId: vm.relation.id,
                companyId: vm.relation.companyId,
              },
            }).then(function(modal) {
              modal.element.modal();
              modal.element.on('hidden.bs.modal', function(e) {
                modal.element.off(e); //Remove event
                modal.element.remove(); //Remove element
              });
              modal.close.then(function(result) {
                if (result) {
                  angular.extend(document, result);
                }
              });
            });
          };

          vm.uploadingFiles = [];
          vm.fileUploaded = function(file) {
            if (!file) return;
            var data = new FormData();
            data.append('relationId', vm.relation.id);
            data.append('documentType', -1);
            data.append('data', file.data);
            var fileInfo = {
              status: 1,
              name: file.name,
              error: null,
            };
            vm.uploadingFiles.push(fileInfo);

            $http({
              method: 'POST',
              url: appUrls.documents + '/api/documents/',
              headers: {
                'Content-Type': undefined,
              },
              data,
            })
              .then(function(response) {
                var result = response.data;
                flySvc.success('UploadSuccessful');
                fileInfo.status = 2;
                if (vm.relation && typeof vm.relation.numberOfDocuments === 'number')
                  vm.relation.numberOfDocuments += 1;

                $timeout(function() {
                  var idx = vm.uploadingFiles.indexOf(fileInfo);
                  if (idx >= 0) vm.uploadingFiles.splice(idx, 1);
                }, 2000);
              })
              .catch(function(error) {
                fileInfo.status = -1;
                fileInfo.error = flySvc.getErrorMsg(
                  error.data.code,
                  error.data.parameters,
                  null,
                  error.data.type,
                );
                flySvc.error(error);
              });
          };
          vm.uploadFiles = function(files) {
            //upload multiple files
            if (!files || !files.length) return;
            files.forEach(file => {
              vm.fileUploaded(file);
            });
          };

          const complianceUpdateHandler = relationId => {
            if (relationId !== vm.relation.id) return;
            $timeout(function() {
              refreshCompliancy();
            }, 0);
          };

          const relationDocumentProcessedHandler = relationId => {
            if (relationId !== vm.relation.id) return;
            $timeout(() => vm.refreshOpenSections(), 0);
          };

          SignalRService.subscribe('compliancyUpdated', complianceUpdateHandler);

          SignalRService.subscribe('relationDocumentProcessed', relationDocumentProcessedHandler);
          $scope.$on('$destroy', function() {
            SignalRService.unsubscribe('compliancyUpdated', complianceUpdateHandler);
            SignalRService.unsubscribe(
              'relationDocumentProcessed',
              relationDocumentProcessedHandler,
            );
          });

          vm.expandAllSections = function() {
            if (!vm.relation || !vm.relation.sections) return;
            var collapsedSections = vm.relation.sections.filter(
              section => !!vm.relation.collapsedSections[section.id],
            );
            if (collapsedSections.length === 0) return;
            relBindSvc.loadSections(
              vm.relId,
              collapsedSections.map(x => x.id),
            );
            vm.relation.sections.forEach(x => (vm.relation.collapsedSections[x.id] = false));
          };
          vm.collapseAllSections = function() {
            if (!vm.relation || !vm.relation.sections) return;
            Object.keys(vm.relation.collapsedSections).forEach(
              x => (vm.relation.collapsedSections[x] = true),
            );
          };

          vm.updateFromExternalProvider = function(actionName) {
            if (vm.moreActionsRunning) return;
            vm.moreActionsRunning = true;
            $http.post(`${appUrls.api}/api/relation/${actionName}?id=${vm.relation.id}`).then(
              function(result) {
                vm.moreActionsRunning = false;
                flySvc.success();
                vm.refreshOpenSections();
              },
              function(error) {
                vm.moreActionsRunning = false;
                flySvc.error(error);
              },
            );
          };
          vm.updateFromEstox = () => vm.updateFromExternalProvider('RetrieveFromEstox');
          vm.updateFromCompanyWeb = () => vm.updateFromExternalProvider('RetrieveFromCompanyWeb');
          vm.updateFromCompanyInfo = () => vm.updateFromExternalProvider('RetrieveFromCompanyInfo');
          vm.updateFromOpenTheBox = () => vm.updateFromExternalProvider('RetrieveFromOpenTheBox');
          vm.updateFromKBO = () => vm.updateFromExternalProvider('RetrieveFromKbo');

          vm.updateFromConnectPlatform = function(platform) {
            if (vm.moreActionsRunning) return;
            vm.moreActionsRunning = true;
            $http
              .post(
                `${appUrls.api}/api/relation/RetrieveFromPlatform/${vm.relation.id}/${platform}`,
              )
              .then(
                function(result) {
                  vm.moreActionsRunning = false;
                  flySvc.success();
                  vm.refreshOpenSections();
                },
                function(error) {
                  vm.moreActionsRunning = false;
                  flySvc.error(error);
                },
              );
          };

          vm.showInvoiceHistory = function() {
            return invoiceDialogsSvc.showInvoicingHistory(
              vm.relation.invoiceStreamId,
              null,
              vm.relation.id,
            );
          };

          vm.disableCreateRegistration = false;
          vm.createRegistration = function() {
            if (vm.disableCreateRegistration) return;
            vm.disableCreateRegistration = true;
            var reg = new RegistrationGroup();
            reg.date = moment
              .utc()
              .startOf('day')
              .toDate();
            reg.referenceYear = moment.utc().year();

            const relation = {
              ...compactRelation(vm.relation),
              invoiceStreamId: vm.relation.invoiceStreamId,
            };
            reg.relation = relation;
            reg.setRelation();
            const userId = dataCache.getArray('employee').userID;

            $q.all([
              invoiceStreamNamesSvc.get(vm.relation.id),
              registrationService.getNextRegistrationTime(
                moment
                  .utc()
                  .startOf('day')
                  .toDate(),
              ),
            ])
              .then(function(results) {
                var streams = results[0];
                var data = results[1];
                reg.invoiceStreamId = getFirstSelectedInvoiceStreamId(
                  streams,
                  vm.relation.invoiceStreamId,
                );
                if (data) reg.work.initTime(data.Hour, data.Minute, 0);

                reg.setUser(userId);

                var vat = dataCache.getDefaultCompany().resourceVATRate || 0.21;
                reg.work.setVatRate(vat, 0);
                reg.cost.setVatRate(vat, 0);
                reg.travel.setVatRate(vat, 0);
                return regDialogsSvc.editRegistrationGroup(reg).then(function(result) {
                  if (result) {
                    flySvc.success('Registration created');
                  }
                });
              })
              .finally(() => {
                vm.disableCreateRegistration = false;
              });
          };
          relBudgetSvc.getBudgetInfo(vm.relation.id, true).then(x => {
            vm.relationBudget = x;
          });
          vm.reloadBudget = function() {
            relBudgetSvc.reload(vm.relation.id);
          };

          vm.createTodo = function() {
            vm.showCreateTodoModal = true;
          };
          vm.onCreateTodoModalClosed = () => {
            $timeout(function() {
              vm.showCreateTodoModal = false;
            }, 0);
          };
          vm.onCreateTodoModalSaved = todo => {
            $timeout(function() {
              vm.showCreateTodoModal = false;
              vm.getRelationKPIData();
              vm.refreshOpenSections();
            }, 0);
          };

          vm.openSharepointLinkModal = () => {
            vm.showSharepointLinkModal = true;
          };
          vm.onSharepointLinkModalClosed = () => {
            $timeout(function() {
              vm.showSharepointLinkModal = false;
            }, 0);
          };
        };
      },
    ],
  };
});
